import type { ColumnDef } from "@tanstack/react-table";
import {
  ArrowUpDown,
  Bot,
  Code,
  HelpCircle,
  MoreHorizontal,
  Plug,
  Trash2,
} from "lucide-react";
import * as React from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import {
  FpDropdownMenu,
  FpDropdownMenuContent,
  FpDropdownMenuItem,
  FpDropdownMenuLabel,
  FpDropdownMenuSeparator,
  FpDropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Switch } from "@/components/ui/switch";
import { useQuery } from "@tanstack/react-query";
import {
  listPavilionAgentToolsQueryOptions,
  useRemoveMcpServer,
  useUpdatePavilionAgentTools,
} from "../../lib/hooks/usePavilionAgents";

// Tool data structure adapted from API response
export interface PavilionTool {
  name: string;
  enabled: boolean;
  namespace: string;
  origin: "native" | "mcp" | "integration";
  serverId?: string;
  serverName?: string;
  description: string;
}

// Helper functions for rendering
const getOriginIcon = (origin: PavilionTool["origin"]) => {
  switch (origin) {
    case "native":
      return <Code className="h-4 w-4" />;
    case "mcp":
      return <Bot className="h-4 w-4" />;
    case "integration":
      return <Plug className="h-4 w-4" />;
    default:
      return <HelpCircle className="h-4 w-4" />;
  }
};

// Create column definitions with props for callbacks
export const createToolsColumns = (
  onToolToggle: (name: string, namespace: string, enabled: boolean) => void,
  onRemoveMcpServer?: (serverId: string) => void,
): ColumnDef<PavilionTool>[] => [
  {
    accessorKey: "enabled",
    header: "Status",
    cell: ({ row }) => (
      <div className="flex items-center gap-2">
        <Switch
          checked={row.getValue("enabled")}
          onCheckedChange={(checked) => {
            onToolToggle(row.original.name, row.original.namespace, checked);
          }}
        />
      </div>
    ),
  },
  {
    accessorKey: "name",
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
      >
        Tool Name
        <ArrowUpDown className="ml-2 h-4 w-4" />
      </Button>
    ),
    cell: ({ row }) => (
      <div className="flex items-center gap-2">
        {getOriginIcon(row.original.origin)}
        <span className="font-medium">{row.getValue("name")}</span>
      </div>
    ),
  },
  {
    id: "description",
    header: "Description",
    cell: ({ row }) => (
      <div className="max-w-[300px] truncate text-sm text-muted-foreground">
        {row.original.description}
      </div>
    ),
  },
  {
    accessorKey: "origin",
    header: "Origin",
    cell: ({ row }) => (
      <Badge variant="outline">{row.getValue("origin")}</Badge>
    ),
  },
  {
    id: "server",
    header: "Server",
    cell: ({ row }) => {
      const tool = row.original;
      if (tool.origin === "mcp" && tool.serverName) {
        return (
          <Badge variant="secondary" className="max-w-[120px] truncate">
            {tool.serverName}
          </Badge>
        );
      }
      return <span className="text-muted-foreground">-</span>;
    },
  },
  {
    id: "actions",
    enableHiding: false,
    cell: ({ row }) => {
      const tool = row.original;

      return (
        <FpDropdownMenu>
          <FpDropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <span className="sr-only">Open menu</span>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </FpDropdownMenuTrigger>
          <FpDropdownMenuContent align="end">
            <FpDropdownMenuLabel>Actions</FpDropdownMenuLabel>
            <FpDropdownMenuItem
              onClick={() => navigator.clipboard.writeText(tool.name)}
            >
              Copy tool name
            </FpDropdownMenuItem>
            {tool.origin === "mcp" && tool.serverId && onRemoveMcpServer && (
              <>
                <FpDropdownMenuSeparator />
                <FpDropdownMenuItem
                  className="text-red-600"
                  onClick={() =>
                    tool.serverId && onRemoveMcpServer(tool.serverId)
                  }
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Remove MCP server
                </FpDropdownMenuItem>
              </>
            )}
          </FpDropdownMenuContent>
        </FpDropdownMenu>
      );
    },
  },
];

// Tools table component
interface ToolsTableProps {
  agentName: string;
}

export function ToolsTable({ agentName }: ToolsTableProps) {
  // Fetch tools data
  const { data: toolsResponse, isLoading } = useQuery(
    listPavilionAgentToolsQueryOptions(agentName),
  );

  // Mutations
  const updateToolsMutation = useUpdatePavilionAgentTools();
  const removeMcpServerMutation = useRemoveMcpServer();

  // Transform API data to component format
  const allTools = React.useMemo(() => {
    const toolsData = toolsResponse?.data;
    if (!toolsData) {
      return [];
    }

    const tools: PavilionTool[] = [];

    // Add native tools
    for (const tool of toolsData.native) {
      tools.push({
        name: tool.name,
        enabled: tool.enabled,
        namespace: tool.namespace,
        origin: "native",
        description: tool.description,
      });
    }

    // Add MCP tools
    for (const tool of toolsData.mcp) {
      tools.push({
        name: tool.name,
        enabled: tool.enabled,
        namespace: tool.namespace,
        origin: "mcp",
        serverId: tool.serverId,
        serverName: tool.serverName,
        description: tool.description,
      });
    }

    // Add integration tools (Slack, etc.)
    const integrationTools = (
      toolsData as typeof toolsData & {
        integrations?: Array<{
          name: string;
          enabled: boolean;
          namespace: string;
        }>;
      }
    ).integrations;
    if (integrationTools) {
      for (const tool of integrationTools) {
        tools.push({
          name: tool.name,
          enabled: tool.enabled,
          namespace: tool.namespace,
          origin: "integration",
          description: tool.description,
        });
      }
    }

    return tools;
  }, [toolsResponse]);

  // Sort tools: enabled tools first, then disabled
  const sortedTools = React.useMemo(() => {
    return [...allTools].sort((a, b) => {
      // Enabled tools (true) should come before disabled tools (false)
      if (a.enabled === b.enabled) {
        // If both have same enabled status, sort alphabetically by name
        return a.name.localeCompare(b.name);
      }
      return b.enabled ? 1 : -1;
    });
  }, [allTools]);

  // Handle tool toggle
  const handleToolToggle = (
    name: string,
    namespace: string,
    enabled: boolean,
  ) => {
    updateToolsMutation.mutate({
      agentName,
      tools: [{ name, namespace, enabled }],
    });
  };

  // Handle remove MCP server
  const handleRemoveMcpServer = (serverId: string) => {
    removeMcpServerMutation.mutate({
      agentName,
      serverId,
    });
  };

  if (isLoading) {
    return <div>Loading tools...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Tools</h3>
          <p className="text-sm text-muted-foreground">
            Manage tools available to this agent
          </p>
        </div>
      </div>

      <DataTable
        columns={createToolsColumns(handleToolToggle, handleRemoveMcpServer)}
        data={sortedTools}
        globalFilterPlaceholder="Search tools..."
        enableRowSelection={false}
        enableGlobalFilter={true}
        enableColumnVisibility={false}
        enablePagination={true}
      />
    </div>
  );
}
